import * as React from 'react'
import {
  BlockLabel,
  BlockPropertiesPanelWrapper,
  BlockWrapper,
} from './block-wrapper'
import { BlockEmptyState } from './empty-state'
import {
  VariantData,
  getValueBasedOnSelectedVariants,
  isComponentBlock,
  isDocumentBlock,
  isInstanceBlock,
  isListBlock,
  isReferenceBlock,
  type ComponentBlock,
  type DocumentBlock,
  type InstanceBlock,
  type ReferenceBlock as ReferenceBlockType,
} from '@basehub/data/replicache/index'
import { getInvalidReferencedBlock } from './helpers'
import { clsx } from 'clsx'
import { EditorModeProvider, useEditorMode } from '../editor-mode-provider'
import { PropertiesPanel as InstanceBlockPropertiesPanel } from './instance'
import { useEditorDndTreeNode } from './document/dnd'
import { ChevronRightIcon, CaretSortIcon } from '@radix-ui/react-icons'
import { DocumentPropertiesPanel } from './document/properties-panel'
import { ListBlockViewIcon } from './list/views/view-switcher'
import { getViewDefaultTitle } from '@basehub/data/replicache/schema/block/list/utils'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'

import * as BooleanBlock from './boolean'
import * as CodeSnippetBlock from './code-snippet'
import * as DateBlock from './date'
import * as NestedDocumentBlock from './document/nested'
import * as MediaBlock from './media'
import * as ListBlock from './list'
import * as NumberBlock from './number'
import * as RichTextBlock from './rich-text'
import * as TextBlock from './text'
import * as CommentBlock from './comment'
import * as ReferenceBlock from './reference'
import * as SelectBlock from './select'
import * as IconBlock from './icon'
import * as ColorBlock from './color'
import * as OgImageBlock from './og-image'
import * as VariantSetBlock from './variant-set'
import * as EventBlock from './event'
import * as WorkflowBlock from './workflow'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { Skeleton } from '~/sotano-kit/primitives/skeleton'

/* -------------------------------------------------------------------------------------------------
 * Blocks — main component of this file
 * -----------------------------------------------------------------------------------------------*/

const LOG_PERF = false

export type BlocksProps = {
  block: DocumentBlock | ComponentBlock | InstanceBlock | ReferenceBlockType
  path: string
  variant?: 'default' | 'nested' | 'nested-union'
  selectedDashboardVariants?: VariantData[]
  isLoadingReferenceTargets?: boolean
}

export type BlockVariant = NonNullable<BlocksProps['variant']>

export const Blocks = ({
  block: parentBlock,
  variant = 'default',
  selectedDashboardVariants,
  isLoadingReferenceTargets,
}: BlocksProps) => {
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const getPath = useWorkingTreeStore((s) => s.tree?.getPath)
  const rootId = useWorkingTreeStore((s) => s.tree?.root.value.id ?? '')

  const path = getPath?.(parentBlock.id) ?? ''

  if (LOG_PERF) {
    // eslint-disable-next-line no-console
    console.time('getBlocksToRender')
  }

  const childrenKeys = useWorkingTreeStore(
    (s) => {
      if (isReferenceBlock(parentBlock)) {
        const targetBlockIds = selectedDashboardVariants
          ? getValueBasedOnSelectedVariants({
              block: parentBlock,
              variantsToFilterBy: selectedDashboardVariants,
            }).targetBlockIds
          : parentBlock.value.targetBlockIds

        return targetBlockIds
      }

      return s.tree?.getChildrenKeys(parentBlock.id) ?? []
    },
    (prev, next) => {
      let changed = false

      for (let i = 0; i < Math.max(prev?.length ?? 0, next?.length ?? 0); i++) {
        if (prev?.[i] !== next?.[i]) {
          changed = true
          break
        }
      }

      // if no changes, prevent recalc, else recalculate
      return !changed
    }
  )

  if (LOG_PERF) {
    // eslint-disable-next-line no-console
    console.timeEnd('getBlocksToRender')
  }

  const editorModeParent = useEditorMode()

  useEditorDndTreeNode({
    rootId,
    enabled:
      Boolean(parentBlock) &&
      (editorModeParent === 'write:all' || variant === 'nested-union'),
    allowedType:
      variant === 'nested-union' ? 'editor-dnd:reference' : 'editor-dnd',
    currentParentId: parentBlock.id,
  })

  if (childrenKeys.length < 1) {
    if (variant === 'default' && editorModeParent === 'write:all') {
      const parentKey = path.split('/')[path.split('/').length - 2]
      const parent = getNode?.(parentKey ?? '')
      return (
        <BlockEmptyState
          block={parentBlock}
          path={path}
          parent={parent?.value ?? null}
        />
      )
    } else if (variant === 'nested') {
      return (
        <div className="text-xs text-dark-muted h-[40.5px] flex items-center">
          <span>Empty block</span>
        </div>
      )
    } else return null
  }

  return (
    <EditorModeProvider block={null} parentBlock={parentBlock}>
      {/* eslint-disable-next-line react-compiler/react-compiler */}
      {childrenKeys.map((childKey, index) => {
        const node = getNode?.(childKey)
        return (
          <BlockSwitch
            key={node?.value ? idToSourceBlockId(node.value) : childKey}
            childKey={childKey}
            path={path}
            index={index}
            parent={parentBlock}
            variant={variant}
            isLoadingReferenceTargets={isLoadingReferenceTargets}
          />
        )
      })}
    </EditorModeProvider>
  )
}

/* -------------------------------------------------------------------------------------------------
 * Block Switch
 * -----------------------------------------------------------------------------------------------*/

/**
 * If you add a new block here add it to preview switch below this one
 */
export const BlockSwitch = ({
  childKey,
  path,
  index,
  parent,
  variant = 'default',
  isLoadingReferenceTargets,
}: {
  childKey: string
  path: string
  index: number
  parent: BlocksProps['block']
  variant?: BlockVariant
  isLoadingReferenceTargets?: boolean
}) => {
  const node = useWorkingTreeStore((s) => s.tree?.getNode(childKey))
  const editorMode = useEditorMode()

  if (!node) {
    if (isReferenceBlock(parent)) {
      const block = getInvalidReferencedBlock(childKey)
      if (block.id.startsWith('invalid-reference')) {
        if (isLoadingReferenceTargets) {
          return (
            <div key={block.id} className="w-full flex">
              <Skeleton
                height={43}
                className="w-full mx-2 my-2 rounded-md"
                borderless
              />
            </div>
          )
        }
        return (
          <BlockWrapper
            key={block.id}
            variant={variant}
            block={block}
            path={path}
            focusableElementSelector="a"
            className={clsx(
              variant === 'nested' || variant === 'nested-union'
                ? '!py-2'
                : '!py-4'
            )}
            contentClassName={clsx(
              '!py-0 !px-0 !rounded-md !mx-0',
              variant === 'nested' || variant === 'nested-union' ? '' : '-my-2'
            )}
            actionsClassName={clsx(
              variant === 'nested-union'
                ? '!top-[17.5px] mr-1.5'
                : '!top-[25.5px]'
            )}
            index={index}
            parentBlock={parent}
          >
            <NestedDocumentBlock.Link
              disabled
              mode="readonly"
              key={block.id + '-invalid-reference-inner'}
              block={block as ComponentBlock}
              parentBlock={parent}
              className="[&>span]:!text-dark-error"
            />
          </BlockWrapper>
        )
      }
    }
    return null
  }

  const block = node.value
  const isReadOnly = editorMode === 'preview'
  const key = `${block.sourceBlockId || block.id}-${block.type}`

  switch (block.type) {
    case 'text': {
      return (
        <BlockWrapper
          key={key}
          variant={variant}
          block={block}
          path={path}
          focusableElementSelector="[data-text-block]"
          index={index}
          parentBlock={parent}
        >
          <BlockLabel
            parentBlock={parent}
            block={block}
            path={path}
            index={index}
          />
          <TextBlock.Input
            block={block}
            path={path}
            parent={parent}
            readOnly={isReadOnly}
          />
          <BlockPropertiesPanelWrapper
            parentBlock={parent}
            block={block}
            path={path}
          >
            <TextBlock.PropertiesPanel
              block={block}
              path={path}
              parent={parent}
            />
          </BlockPropertiesPanelWrapper>
        </BlockWrapper>
      )
    }
    case 'number':
      return (
        <BlockWrapper
          key={key}
          variant={variant}
          block={block}
          path={path}
          focusableElementSelector="input[data-number-block]"
          index={index}
          parentBlock={parent}
        >
          <BlockLabel
            parentBlock={parent}
            block={block}
            path={path}
            index={index}
          />
          <NumberBlock.Input
            block={block}
            parent={parent}
            path={path}
            readOnly={isReadOnly}
          />
          <BlockPropertiesPanelWrapper
            parentBlock={parent}
            block={block}
            path={path}
          >
            <NumberBlock.PropertiesPanel
              block={block}
              path={path}
              parent={parent}
            />
          </BlockPropertiesPanelWrapper>
        </BlockWrapper>
      )
    case 'select':
      return (
        <BlockWrapper
          key={key}
          variant={variant}
          block={block}
          path={path}
          focusableElementSelector="input[data-selector-search-input]"
          index={index}
          parentBlock={parent}
        >
          <BlockLabel
            parentBlock={parent}
            block={block}
            path={path}
            index={index}
          />
          <SelectBlock.Input
            block={block}
            readOnly={isReadOnly}
            path={path}
            parent={parent}
            canCreateNewOptions={!isInstanceBlock(parent)}
          />
          <BlockPropertiesPanelWrapper
            parentBlock={parent}
            block={block}
            path={path}
          >
            <SelectBlock.PropertiesPanel
              block={block}
              path={path}
              parent={parent}
            />
          </BlockPropertiesPanelWrapper>
        </BlockWrapper>
      )
    case 'comment':
      return (
        <BlockWrapper
          key={key}
          variant={variant}
          block={block}
          path={path}
          focusableElementSelector="div[contenteditable]"
          className="!py-0"
          actionsClassName="!top-2"
          presenceTop={5}
          contentClassName="!py-0"
          noSelectOnClick
          index={index}
          parentBlock={parent}
        >
          <CommentBlock.Input block={block} path={path} parentBlock={parent} />
        </BlockWrapper>
      )
    case 'rich-text': {
      return (
        <BlockWrapper
          key={key}
          variant={variant}
          block={block}
          path={path}
          focusableElementSelector="div[contenteditable]"
          index={index}
          parentBlock={parent}
        >
          <BlockLabel
            parentBlock={parent}
            block={block}
            path={path}
            index={index}
          />
          <RichTextBlock.Input
            block={block}
            path={path}
            parent={parent}
            variant={variant}
          />
          <BlockPropertiesPanelWrapper
            parentBlock={parent}
            block={block}
            path={path}
          >
            <RichTextBlock.PropertiesPanel
              block={block}
              path={path}
              parent={parent}
            />
          </BlockPropertiesPanelWrapper>
        </BlockWrapper>
      )
    }
    case 'date': {
      return (
        <BlockWrapper
          key={key}
          variant={variant}
          block={block}
          path={path}
          focusableElementSelector="input[data-date-block]"
          index={index}
          parentBlock={parent}
        >
          <BlockLabel
            parentBlock={parent}
            block={block}
            path={path}
            index={index}
          />
          <DateBlock.Input block={block} parent={parent} path={path} />
          <BlockPropertiesPanelWrapper
            parentBlock={parent}
            block={block}
            path={path}
          >
            <DateBlock.PropertiesPanel
              block={block}
              path={path}
              parent={parent}
            />
          </BlockPropertiesPanelWrapper>
        </BlockWrapper>
      )
    }
    case 'color': {
      return (
        <BlockWrapper
          key={key}
          variant={variant}
          block={block}
          path={path}
          focusableElementSelector="button[data-color-block]"
          index={index}
          parentBlock={parent}
        >
          <BlockLabel
            parentBlock={parent}
            block={block}
            path={path}
            index={index}
          />
          <ColorBlock.Input block={block} parent={parent} path={path} />
          <BlockPropertiesPanelWrapper
            parentBlock={parent}
            block={block}
            path={path}
          >
            <ColorBlock.PropertiesPanel
              block={block}
              path={path}
              parent={parent}
            />
          </BlockPropertiesPanelWrapper>
        </BlockWrapper>
      )
    }
    case 'media': {
      return (
        <BlockWrapper
          key={key}
          variant={variant}
          block={block}
          path={path}
          focusableElementSelector="input[data-media-block]"
          index={index}
          parentBlock={parent}
        >
          <BlockLabel
            parentBlock={parent}
            block={block}
            path={path}
            index={index}
          />
          <MediaBlock.Input block={block} path={path} parent={parent} />
          <BlockPropertiesPanelWrapper
            parentBlock={parent}
            block={block}
            path={path}
            blockTitleGroup={false}
          >
            <MediaBlock.PropertiesPanel
              block={block}
              parent={parent}
              path={path}
              focusableElementSelector="input[data-media-block]"
            />
          </BlockPropertiesPanelWrapper>
        </BlockWrapper>
      )
    }
    case 'boolean': {
      return (
        <BlockWrapper
          key={key}
          variant={variant}
          block={block}
          path={path}
          focusableElementSelector="button[data-state='checked']"
          index={index}
          parentBlock={parent}
        >
          <BlockLabel
            parentBlock={parent}
            block={block}
            path={path}
            index={index}
          />
          <BooleanBlock.Input block={block} path={path} parent={parent} />
          <BlockPropertiesPanelWrapper
            parentBlock={parent}
            block={block}
            path={path}
          >
            <BooleanBlock.PropertiesPanel
              block={block}
              path={path}
              parent={parent}
            />
          </BlockPropertiesPanelWrapper>
        </BlockWrapper>
      )
    }
    case 'code-snippet': {
      return (
        <BlockWrapper
          key={key}
          variant={variant}
          block={block}
          path={path}
          focusableElementSelector="[contenteditable].cm-content"
          index={index}
          parentBlock={parent}
        >
          <BlockLabel
            parentBlock={parent}
            block={block}
            path={path}
            index={index}
          />
          <CodeSnippetBlock.Input block={block} parent={parent} path={path} />
          <BlockPropertiesPanelWrapper
            parentBlock={parent}
            block={block}
            path={path}
          >
            <CodeSnippetBlock.PropertiesPanel
              block={block}
              path={path}
              parent={parent}
            />
          </BlockPropertiesPanelWrapper>
        </BlockWrapper>
      )
    }
    case 'og-image': {
      return (
        <BlockWrapper
          key={key}
          variant={variant}
          block={block}
          path={path}
          focusableElementSelector="[data-og-image-block]"
          index={index}
          parentBlock={parent}
        >
          <BlockLabel
            parentBlock={parent}
            block={block}
            path={path}
            index={index}
          />
          <OgImageBlock.Input block={block} path={path} parent={parent} />
          <BlockPropertiesPanelWrapper
            parentBlock={parent}
            block={block}
            path={path}
          >
            <OgImageBlock.PropertiesPanel
              block={block}
              path={path}
              parent={parent}
            />
          </BlockPropertiesPanelWrapper>
        </BlockWrapper>
      )
    }
    case 'list': {
      return (
        <BlockWrapper
          variant={variant}
          block={block}
          path={path}
          focusableElementSelector="button[data-new-row]"
          index={index}
          parentBlock={parent}
        >
          <BlockLabel
            parentBlock={parent}
            block={block}
            path={path}
            index={index}
            afterTitle={
              <span className="inline-flex items-center">
                <ChevronRightIcon
                  className="inline mr-2"
                  width={14}
                  height={14}
                />

                <ListBlock.ViewSwitcher
                  block={block}
                  path={path}
                  position={{ type: 'first' }}
                >
                  {(selectedView) => {
                    const defaultViewTitle = getViewDefaultTitle(selectedView)

                    return (
                      <span
                        className="flex max-w-[200px] text-dark-bright cursor-default items-center justify-between space-x-1.5 overflow-hidden text-ellipsis whitespace-nowrap rounded-md border border-dark-border-solid bg-dark-shade px-1.5 py-1 text-xs leading-4 outline-1 outline-offset-2 outline-dark-control-base hover:bg-dark-shade-hover focus-visible:outline disabled:cursor-not-allowed disabled:opacity-30"
                        aria-label="Switch List View"
                      >
                        <ListBlockViewIcon
                          view={selectedView ?? { layout: 'table' }}
                          iconProps={{
                            width: 14,
                            height: 14,
                            className: 'shrink-0',
                          }}
                        />
                        <span className="overflow-hidden text-ellipsis">
                          {selectedView?.title || defaultViewTitle}
                        </span>
                        <span>
                          <CaretSortIcon className="h-4 w-4 text-dark-muted" />
                        </span>
                      </span>
                    )
                  }}
                </ListBlock.ViewSwitcher>
              </span>
            }
          />
          <ListBlock.Input
            block={block}
            parent={parent}
            path={path}
            variant={variant}
            fullView={false}
          />
          <BlockPropertiesPanelWrapper
            parentBlock={parent}
            block={block}
            path={path}
          >
            <ListBlock.PropertiesPanel
              block={block}
              path={path}
              parent={parent}
            />
          </BlockPropertiesPanelWrapper>
        </BlockWrapper>
      )
    }
    case 'instance':
    case 'component':
    case 'document': {
      return (
        <BlockWrapper
          key={key}
          variant={variant}
          block={block}
          path={path}
          focusableElementSelector="a"
          className={clsx(
            variant === 'nested' || variant === 'nested-union'
              ? '!py-2'
              : '!py-4'
          )}
          contentClassName={clsx(
            '!py-0 !px-0 !rounded-md !mx-0',
            variant === 'nested' || variant === 'nested-union' ? '' : '-my-2'
          )}
          actionsClassName={clsx(
            variant === 'nested-union'
              ? '!top-[17.5px] mr-1.5'
              : '!top-[25.5px]'
          )}
          index={index}
          parentBlock={parent}
        >
          <NestedDocumentBlock.Link
            mode="withActions"
            key={key}
            block={block}
            path={path}
            parentBlock={parent}
            wrapperClassName="mx-0"
          />
          <BlockPropertiesPanelWrapper
            parentBlock={parent}
            block={block}
            path={path}
            blockTitleGroup={{
              forceEditable:
                block.type === 'instance' &&
                (isListBlock(parent) || isReferenceBlock(parent)),
            }}
          >
            {isInstanceBlock(block) && (
              <InstanceBlockPropertiesPanel
                block={block}
                parent={parent}
                path={path}
                strict
              />
            )}
            {(isDocumentBlock(block) || isComponentBlock(block)) && (
              <DocumentPropertiesPanel
                block={block}
                parent={parent}
                path={path}
                strict
              />
            )}
          </BlockPropertiesPanelWrapper>
        </BlockWrapper>
      )
    }
    case 'reference': {
      return (
        <BlockWrapper
          key={key}
          variant={variant}
          block={block}
          path={path}
          focusableElementSelector="input[data-selector-search-input]"
          index={index}
          parentBlock={parent}
        >
          <BlockLabel
            parentBlock={parent}
            block={block}
            path={path}
            index={index}
          />
          <ReferenceBlock.Input path={path} block={block} parent={parent} />
          <BlockPropertiesPanelWrapper
            parentBlock={parent}
            block={block}
            path={path}
          >
            <ReferenceBlock.PropertiesPanel
              block={block}
              path={path}
              parent={parent}
            />
          </BlockPropertiesPanelWrapper>
        </BlockWrapper>
      )
    }
    case 'variant-set': {
      return (
        <BlockWrapper
          key={key}
          variant={variant}
          block={block}
          path={path}
          focusableElementSelector="[data-selector-search-input]"
          index={index}
          parentBlock={parent}
        >
          <BlockLabel
            parentBlock={parent}
            block={block}
            path={path}
            index={index}
          />
          <VariantSetBlock.StoreProvider>
            <VariantSetBlock.Input path={path} block={block} parent={parent} />
            <BlockPropertiesPanelWrapper
              parentBlock={parent}
              block={block}
              path={path}
            >
              <VariantSetBlock.PropertiesPanel block={block} path={path} />
            </BlockPropertiesPanelWrapper>
          </VariantSetBlock.StoreProvider>
        </BlockWrapper>
      )
    }
    case 'event': {
      return (
        <BlockWrapper
          key={key}
          variant={variant}
          block={block}
          path={path}
          focusableElementSelector="[data-selector-search-input]"
          index={index}
          parentBlock={parent}
        >
          <BlockLabel
            parentBlock={parent}
            block={block}
            path={path}
            index={index}
          />

          <EventBlock.Input
            block={block}
            variant={variant}
            path={path}
            parentBlock={parent}
          />
          <BlockPropertiesPanelWrapper
            parentBlock={parent}
            block={block}
            path={path}
          >
            <EventBlock.PropertiesPanel block={block} path={path} />
          </BlockPropertiesPanelWrapper>
        </BlockWrapper>
      )
    }
    case 'icon': {
      return (
        <BlockWrapper
          key={key}
          variant={variant}
          block={block}
          path={path}
          focusableElementSelector="[contenteditable].cm-content"
          index={index}
          parentBlock={parent}
        >
          <BlockLabel
            parentBlock={parent}
            block={block}
            path={path}
            index={index}
          />
          <IconBlock.Input
            block={block}
            path={path}
            parent={parent}
            readOnly={isReadOnly}
            showControls
          />
          <BlockPropertiesPanelWrapper
            parentBlock={parent}
            block={block}
            path={path}
          >
            <IconBlock.PropertiesPanel
              block={block}
              path={path}
              parent={parent}
            />
          </BlockPropertiesPanelWrapper>
        </BlockWrapper>
      )
    }
    case 'workflow': {
      return (
        <BlockWrapper
          key={key}
          variant={variant}
          block={block}
          path={path}
          focusableElementSelector="[data-selector-search-input]"
          index={index}
          parentBlock={parent}
        >
          <BlockLabel
            parentBlock={parent}
            block={block}
            path={path}
            index={index}
          />
          <WorkflowBlock.Input block={block} path={path} />
          <BlockPropertiesPanelWrapper
            parentBlock={parent}
            block={block}
            path={path}
          >
            <WorkflowBlock.PropertiesPanel block={block} />
          </BlockPropertiesPanelWrapper>
        </BlockWrapper>
      )
    }
    default:
      return null
  }
}

BlockSwitch.displayName = 'BlockSwitch'
